import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/custom/Navbar";
import WeatherWidget from "@/components/custom/WeatherWidget";
import NeedCard from "@/components/custom/NeedCard";
import ChatPanel from "@/components/custom/ChatPanel";
import VolunteerLevel from "@/components/custom/VolunteerLevel";
import MunicipalitySelector from "@/components/custom/MunicipalitySelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Heart, MapPin, Clock, User, Edit3, CheckCircle, XCircle,
  Hourglass, TrendingUp, Shield, Star, PawPrint, Award, Lock,
  MessageCircle, Bell, Search, GraduationCap, Home, Building2, Users,
  ArrowRight,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `2px solid ${C.navy}`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

const BADGE_ICONS = {
  "heart": Heart, "hand-helping": Heart, "shield": Shield, "paw-print": PawPrint,
  "star": Star, "check-circle": CheckCircle, "award": Award, "home": Home,
  "building": Building2, "users": Users,
};

const APRENDE_BADGE_COLORS = {
  "amber": { bg: "#fa9000" }, "blue": { bg: "#4A90D9" }, "purple": { bg: "#B39DDB" },
};

const STATUS_MAP = {
  pendiente: { icon: Hourglass, color: "#fa9000", label: "PENDIENTE" },
  aceptada: { icon: CheckCircle, color: "#8B9A2D", label: "ACEPTADA" },
  rechazada: { icon: XCircle, color: "#e8637a", label: "RECHAZADA" },
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, borderRadius: 4, ...style }}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = C.navy, color = "#fff" }) {
  return (
    <span className="inline-block px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, borderRadius: 3 }}
    >{children}</span>
  );
}

function SectionTitle({ icon: Icon, iconColor, children, color = C.navy, kicker }) {
  return (
    <div className="mb-8">
      {kicker && (
        <p className="text-[11px] font-bold tracking-[0.25em] uppercase mb-2 flex items-center gap-2" style={{ fontFamily: MONO, color: iconColor || C.blue }}>
          <Icon className="w-4 h-4" style={{ color: iconColor || C.blue }} />
          {kicker}
        </p>
      )}
      <h3 className="text-[clamp(1.8rem,3.5vw,2.6rem)] leading-[0.95] tracking-tight" style={{ color, fontFamily: DISPLAY }}>
        {children}
      </h3>
    </div>
  );
}

function FullBleed({ bg, children, testId }) {
  return (
    <section style={{ background: bg, marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }} data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">{children}</div>
    </section>
  );
}

export default function VolunteerDashboard() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [offers, setOffers] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState({ earned: [], locked: [] });
  const [aprendeBadges, setAprendeBadges] = useState([]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [activeTab, setActiveTab] = useState("panel");
  const [profileForm, setProfileForm] = useState({
    name: "", bio: "", picture: "", alert_radius_km: 100,
    search_network_optin: false, location_name: "", location_lat: null, location_lng: null,
  });

  const token = localStorage.getItem("ea_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(() => {
    if (!user) return;
    axios.get(`${API}/offers/mine`, { headers, withCredentials: true }).then((r) => setOffers(r.data)).catch(() => {});
    axios.get(`${API}/needs?status=abierta`).then((r) => setNeeds(r.data.slice(0, 6))).catch(() => {});
    axios.get(`${API}/stats/volunteer/${user.user_id}`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/badges/${user.user_id}`).then((r) => setBadges(r.data)).catch(() => {});
    axios.get(`${API}/aprende/badges`, { headers, withCredentials: true }).then((r) => setAprendeBadges(r.data.badges || [])).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (user) setProfileForm({
      name: user.name || "", bio: user.bio || "", picture: user.picture || "",
      alert_radius_km: user.alert_radius_km || 100, search_network_optin: user.search_network_optin || false,
      location_name: user.location_name || "", location_lat: user.location_lat || null, location_lng: user.location_lng || null,
    });
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      const res = await axios.put(`${API}/auth/profile`, profileForm, { headers, withCredentials: true });
      setUser(res.data);
      localStorage.setItem("ea_user", JSON.stringify(res.data));
      setShowEditProfile(false);
      toast.success("Perfil actualizado");
    } catch { toast.error("Error al guardar perfil"); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="volunteer-dashboard" style={{ background: C.offwhite, fontFamily: B.font }}>
      <Navbar />

      <div className="pt-28 pb-16 max-w-7xl mx-auto px-6">
        {/* ── Profile banner ── */}
        <div className="mb-8 pb-6" style={{ borderBottom: `1px solid ${C.navy}10` }}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-20 h-20 object-cover" style={{ borderRadius: 4, border: `2px solid ${C.navy}` }} />
              ) : (
                <div className="w-20 h-20 flex items-center justify-center" style={{ background: C.navy, borderRadius: 4 }}>
                  <span className="text-white text-3xl font-bold" style={{ fontFamily: DISPLAY }}>{user.name?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
              <div>
                <h1 className="text-3xl md:text-4xl tracking-tight" style={{ color: C.navy, fontFamily: DISPLAY }}>{user.name}</h1>
                <p className="text-xs tracking-widest mt-1.5" style={{ fontFamily: B.mono, color: C.blue }}>{user.email}</p>
              </div>
            </div>
            <button onClick={() => setShowEditProfile(true)} className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all hover:-translate-y-0.5" style={{ fontFamily: B.mono, color: C.navy, border: `2px solid ${C.navy}`, borderRadius: 4, background: "#fff" }} data-testid="edit-profile-btn">
              <Edit3 className="w-4 h-4" /> Editar
            </button>
          </div>
          {user.bio && <p className="text-base mb-4 leading-relaxed max-w-xl" style={{ color: C.gray }}>{user.bio}</p>}
          {/* Inline stats row */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <span className="text-xs font-bold tracking-widest uppercase" style={{ fontFamily: B.mono, background: C.blue, color: "#fff", padding: "4px 14px", borderRadius: 3 }}>Voluntario</span>
            <span className="flex items-center gap-2" data-testid="stat-offers">
              <Heart className="w-5 h-5" style={{ color: "#e8637a" }} />
              <span className="text-2xl font-bold" style={{ color: C.navy, fontFamily: DISPLAY }}>{stats?.total_offers || 0}</span>
              <span className="text-xs tracking-wider" style={{ fontFamily: B.mono, color: C.gray }}>ofertas</span>
            </span>
            <span className="flex items-center gap-2" data-testid="stat-accepted">
              <CheckCircle className="w-5 h-5" style={{ color: "#8B9A2D" }} />
              <span className="text-2xl font-bold" style={{ color: C.navy, fontFamily: DISPLAY }}>{stats?.accepted || 0}</span>
              <span className="text-xs tracking-wider" style={{ fontFamily: B.mono, color: C.gray }}>aceptadas</span>
            </span>
          </div>
        </div>

        {/* ── Weather (amplio, protagonista) ── */}
        <div className="mb-10">
          <WeatherWidget userLat={user?.location_lat} userLng={user?.location_lng} userName={user?.location_name} />
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6" style={{ borderBottom: `1px solid ${C.navy}10` }}>
          {[
            { id: "panel", label: "Mi panel", testId: "vol-tab-panel" },
            { id: "chat", label: "Chat", icon: MessageCircle, testId: "vol-tab-chat" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={tab.testId}
              className="px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] flex items-center gap-2 transition-colors relative"
              style={{
                fontFamily: B.mono,
                background: "transparent",
                color: activeTab === tab.id ? C.navy : C.lightgray,
                borderBottom: activeTab === tab.id ? `2px solid ${C.navy}` : "2px solid transparent",
                marginBottom: "-1px",
              }}
            >
              {tab.icon && <tab.icon className="w-3.5 h-3.5" />}
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "panel" && (
          <>
            {/* Level */}
            <div className="mb-12">
              <SectionTitle icon={TrendingUp} iconColor={C.lilac} kicker="Tu progreso">Mi nivel</SectionTitle>
              <VolunteerLevel />
            </div>

            {/* My offers — bloque ancho ámbar */}
            <FullBleed bg={C.orange} testId="my-offers-block">
              <SectionTitle icon={TrendingUp} iconColor={C.navy} color={C.navy} kicker="Tu actividad solidaria">Mis ayudas</SectionTitle>
              {offers.length > 0 ? (
                <div className="space-y-3">
                  {offers.map((offer) => {
                    const s = STATUS_MAP[offer.status] || STATUS_MAP.pendiente;
                    return (
                      <div key={offer.offer_id} className="p-5 flex items-center justify-between gap-4" style={{ background: "#fff", border: `2px solid ${C.navy}`, borderRadius: 4 }} data-testid={`offer-item-${offer.offer_id}`}>
                        <div>
                          <p className="text-base md:text-lg font-bold" style={{ color: C.navy }}>{offer.need_title || offer.need_id}</p>
                          <p className="text-xs mt-1" style={{ fontFamily: B.mono, color: C.gray }}>
                            {offer.shelter_name && <span>{offer.shelter_name}</span>}
                            {offer.need_type && <span className="ml-2" style={{ color: C.blue }}>{offer.need_type}</span>}
                          </p>
                          {offer.message && <p className="text-sm mt-1.5 italic" style={{ color: C.gray }}>"{offer.message}"</p>}
                          <p className="text-xs mt-1.5 flex items-center gap-1" style={{ fontFamily: B.mono, color: C.lightgray }}>
                            <Clock className="w-3.5 h-3.5" />{new Date(offer.created_at).toLocaleDateString("es-ES")}
                          </p>
                        </div>
                        <BBadge bg={s.color} color="#fff">{s.label}</BBadge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center" style={{ background: "rgba(255,255,255,0.55)", border: `2px solid ${C.navy}`, borderRadius: 4 }}>
                  <Heart className="w-10 h-10 mx-auto mb-3" style={{ color: C.navy }} />
                  <p className="text-base md:text-lg" style={{ color: C.navy }}>
                    Aún no has ofrecido ayuda. Echa un vistazo a las necesidades: cada ofrecimiento cuenta.
                  </p>
                </div>
              )}
            </FullBleed>

            {/* Needs — bloque ancho azul noche */}
            <FullBleed bg={C.navy} testId="recent-needs-block">
              <div className="flex items-end justify-between flex-wrap gap-4">
                <SectionTitle icon={Heart} iconColor={C.orange} color={C.white} kicker="La red te necesita">Necesidades recientes</SectionTitle>
                <button onClick={() => navigate("/needs")} className="mb-8 text-sm font-bold uppercase tracking-wider flex items-center gap-2 transition-transform hover:translate-x-1" style={{ fontFamily: B.mono, color: C.orange }} data-testid="view-all-needs-btn">
                  Ver todas <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {needs.map((need) => <NeedCard key={need.need_id} need={need} onOfferSent={fetchData} />)}
              </div>
              {needs.length === 0 && (
                <div className="py-10 text-center" style={{ borderTop: "2px solid rgba(255,255,255,0.2)" }}>
                  <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.75)" }}>
                    Ahora mismo no hay necesidades activas. Cuando una protectora publique algo cerca de ti, lo verás aquí.
                  </p>
                </div>
              )}
            </FullBleed>

            {/* Badges */}
            <div className="pt-12 mb-12">
              <SectionTitle icon={Award} iconColor={C.orange} kicker="Reconocimientos">Mis badges</SectionTitle>
              <div className="flex flex-wrap gap-3">
                {badges.earned.map((b) => {
                  const Icon = BADGE_ICONS[b.icon] || Star;
                  return (
                    <div key={b.badge_id} className="flex items-center gap-3 px-4 py-3" style={{ background: "#fff", border: `2px solid ${C.navy}`, borderRadius: 4 }} data-testid={`badge-${b.badge_id}`}>
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center" style={{ background: `${b.color}18`, borderRadius: 4 }}>
                        <Icon className="w-5 h-5" style={{ color: b.color }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold leading-tight" style={{ color: C.navy }}>{b.name}</p>
                        <p className="text-xs" style={{ fontFamily: B.mono, color: C.lightgray }}>{b.description}</p>
                      </div>
                    </div>
                  );
                })}
                {badges.locked.map((b) => {
                  const Icon = BADGE_ICONS[b.icon] || Star;
                  return (
                    <div key={b.badge_id} className="flex items-center gap-3 px-4 py-3 opacity-50" style={{ border: `2px dashed ${C.navy}55`, borderRadius: 4 }} data-testid={`badge-locked-${b.badge_id}`}>
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center relative" style={{ background: "#fff", borderRadius: 4 }}>
                        <Icon className="w-5 h-5" style={{ color: "#999" }} />
                        <Lock className="w-3 h-3 absolute -bottom-0.5 -right-0.5" style={{ color: "#999" }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold leading-tight" style={{ color: "#777" }}>{b.name}</p>
                        <p className="text-xs" style={{ fontFamily: B.mono, color: "#999" }}>{b.progress || 0}/{b.threshold}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {badges.earned.length === 0 && badges.locked.length === 0 && (
                <div className="py-8 text-center" style={{ borderTop: `2px solid ${C.navy}20` }}>
                  <Award className="w-10 h-10 mx-auto mb-3" style={{ color: C.lightgray }} />
                  <p className="text-base" style={{ color: C.gray }}>Ofrece tu primera ayuda para desbloquear badges</p>
                </div>
              )}
            </div>

            {/* APRENDE Badges — última sección */}
            <div className="mb-4">
              <SectionTitle icon={GraduationCap} iconColor={C.lilac} kicker="Sigue aprendiendo">Formación Aprende</SectionTitle>
              {aprendeBadges.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {aprendeBadges.map((badge) => {
                    const Icon = BADGE_ICONS[badge.icon] || Award;
                    const colors = APRENDE_BADGE_COLORS[badge.color] || APRENDE_BADGE_COLORS.purple;
                    return (
                      <div key={badge.id} className="flex items-center gap-3 px-4 py-3" style={{ background: "#fff", border: `2px solid ${C.navy}`, borderRadius: 4 }} data-testid={`aprende-badge-${badge.id}`}>
                        <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center" style={{ background: colors.bg, borderRadius: 4 }}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-base font-bold" style={{ color: C.navy }}>{badge.name}</p>
                          <p className="text-xs" style={{ fontFamily: B.mono, color: C.lightgray }}>{badge.description}</p>
                          {badge.completed_at && (
                            <p className="text-xs mt-0.5" style={{ fontFamily: B.mono, color: C.lightgray }}>
                              {new Date(badge.completed_at).toLocaleDateString("es-ES")}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-10 text-center cursor-pointer transition-transform hover:-translate-y-0.5" style={{ background: "#fff", border: `2px solid ${C.navy}`, borderRadius: 4 }} onClick={() => navigate("/aprende")}>
                  <GraduationCap className="w-10 h-10 mx-auto mb-3" style={{ color: C.lilac }} />
                  <p className="text-lg font-bold mb-1.5" style={{ color: C.navy, fontFamily: DISPLAY }}>Empieza tu formación</p>
                  <p className="text-sm" style={{ fontFamily: B.mono, color: C.gray }}>Completa cursos en APRENDE para desbloquear badges</p>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "chat" && <ChatPanel />}
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="sm:max-w-md" style={{ borderRadius: 4, border: B.border }}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight" style={{ fontFamily: B.font, color: "#2D3561" }}>EDITAR PERFIL</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>NOMBRE</Label>
              <Input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className="mt-1" style={{ border: `1px solid ${C.navy}08`, borderRadius: 4 }} data-testid="edit-profile-name" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>BIO</Label>
              <Textarea value={profileForm.bio} onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })} placeholder="Cuéntanos algo sobre ti..." className="mt-1" style={{ border: `1px solid ${C.navy}08`, borderRadius: 4 }} data-testid="edit-profile-bio" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>FOTO URL</Label>
              <Input value={profileForm.picture} onChange={(e) => setProfileForm({ ...profileForm, picture: e.target.value })} placeholder="https://..." className="mt-1" style={{ border: `1px solid ${C.navy}08`, borderRadius: 4 }} data-testid="edit-profile-picture" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest flex items-center gap-2" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                <MapPin className="w-4 h-4" style={{ color: "#e8637a" }} /> MI UBICACIÓN
              </Label>
              <MunicipalitySelector
                value={profileForm.location_name}
                onChange={(name) => setProfileForm({ ...profileForm, location_name: name })}
                onLocationSelect={(lat, lng, name) => { setProfileForm((f) => ({ ...f, location_lat: lat, location_lng: lng, location_name: name })); toast.success(`${name} seleccionado`); }}
                placeholder="Buscar tu municipio..."
                className="mt-1"
              />
              {profileForm.location_lat && (
                <p className="text-[10px] mt-1 flex items-center gap-1" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>
                  <MapPin className="w-3 h-3" />{profileForm.location_lat.toFixed(4)}, {profileForm.location_lng.toFixed(4)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest flex items-center gap-2" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                <Bell className="w-4 h-4" style={{ color: "#686af3" }} /> RADIO DE ALERTAS
              </Label>
              <Select value={String(profileForm.alert_radius_km)} onValueChange={(v) => setProfileForm({ ...profileForm, alert_radius_km: parseInt(v) })}>
                <SelectTrigger className="mt-1" style={{ border: `1px solid ${C.navy}08`, borderRadius: 4 }} data-testid="edit-profile-radius">
                  <SelectValue placeholder="Selecciona radio" />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 4, border: B.border }}>
                  <SelectItem value="25">25 km - Solo muy cerca</SelectItem>
                  <SelectItem value="50">50 km - Mi zona</SelectItem>
                  <SelectItem value="100">100 km - Regional</SelectItem>
                  <SelectItem value="200">200 km - Toda mi comunidad</SelectItem>
                  <SelectItem value="500">500 km - Nacional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div
              className="p-4 cursor-pointer transition-all"
              style={{
                border: profileForm.search_network_optin ? `1px solid #686af3` : `1px solid ${C.navy}12`,
                background: profileForm.search_network_optin ? "#686af310" : "#fff",
              }}
              onClick={() => setProfileForm({ ...profileForm, search_network_optin: !profileForm.search_network_optin })}
              data-testid="search-network-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-6 relative transition-all" style={{ background: profileForm.search_network_optin ? "#686af3" : "#ccc", borderRadius: 12 }}>
                  <div className="w-4 h-4 bg-white absolute top-0.5 transition-all" style={{ left: profileForm.search_network_optin ? "20px" : "2px", borderRadius: "50%" }} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold" style={{ color: "#2D3561" }}>RED DE BÚSQUEDA</p>
                  <p className="text-[10px]" style={{ fontFamily: B.mono, color: "#999" }}>Alertas de animales perdidos cerca (5km)</p>
                </div>
                <Search className="w-5 h-5" style={{ color: profileForm.search_network_optin ? "#686af3" : "#ccc" }} />
              </div>
            </div>
            <button
              onClick={handleSaveProfile}
              className="w-full py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
              style={{ fontFamily: B.font, background: "#686af3", color: "#fff", border: B.border, borderRadius: 4 }}
              data-testid="save-profile-btn"
            >
              GUARDAR CAMBIOS
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
